/**
 * Estado visual da lista da aba Meus Estudos (Flux Personalizado, #1992), derivado do resultado
 * da consulta da lista (estados 1, 2 e 5 do design, .dc.html:396-418 e 444-455):
 * - `initial`: nenhuma lista na tela ainda; mostra o skeleton, sem spinner de tela cheia;
 * - `pending`: a lista na tela fica esmaecida, sem cliques, com a barra fina no topo;
 * - `ready`: lista nítida;
 * - `error-initial`: bloco de erro no lugar da lista;
 * - `error-stale`: faixa discreta acima da lista, que continua na tela.
 */
export type MyStudiesListState = 'initial' | 'pending' | 'ready' | 'error-initial' | 'error-stale';

/**
 * Entradas derivadas do resultado do `useQuery` da lista (a #1999 faz a ligação):
 * `{ isLoading, isFetching, hasError: error !== undefined, hasData: data !== undefined,
 * hasCurrentData: currentData !== undefined }`, mais o `hasManualRefreshPending` da tela.
 */
export interface MyStudiesListStateInput {
  /** `isLoading`: requisição em voo sem nenhum dado (primeira carga ou retry sem lista). */
  isLoading: boolean;
  /** `isFetching`: há uma requisição em voo (primeira carga, troca de argumentos ou refetch). */
  isFetching: boolean;
  /**
   * `error !== undefined`: a última tentativa dos argumentos atuais falhou e nenhuma deu certo
   * depois. NÃO é o `isError` do RTK Query 1.9, que é `status === 'rejected'` e volta a `false`
   * enquanto qualquer novo pedido está em voo; o `error` só sai da entrada quando um pedido dá
   * certo. Com o `isError`, a faixa sumiria a cada refetch por invalidação (excluir, cancelar,
   * refetch tardio de 3 s) e o skeleton piscaria no lugar do bloco de erro.
   */
  hasError: boolean;
  /** `data !== undefined`: há alguma lista para mostrar, mesmo que de argumentos anteriores. */
  hasData: boolean;
  /** `currentData !== undefined`: a lista na tela é a dos argumentos atuais. */
  hasCurrentData: boolean;
  /**
   * O usuário pediu "Atualizar" ou "Tentar de novo" e o refetch ainda não terminou. Quem chama
   * liga a flag no clique e a desliga quando o refetch termina, com sucesso ou com erro.
   */
  hasManualRefreshPending: boolean;
}

/**
 * Decide o estado visual da lista. Regras, na ordem:
 * 1. Sem lista dos argumentos atuais para mostrar (`isLoading`, sem `data`, ou a última tentativa
 *    dos argumentos atuais falhou e a lista guardada é a do filtro anterior): `error-initial` se
 *    falhou e ninguém pediu de novo; senão `initial`. O bloco de erro fica estável durante o
 *    refetch que ninguém pediu (invalidação, refetch tardio de 3 s), e "Tentar de novo" volta ao
 *    skeleton, como no protótipo (.dc.html:728), em vez de esmaecer a lista de outro filtro.
 * 2. Ação do usuário em andamento: argumentos novos em voo (`!hasCurrentData && isFetching`) ou
 *    refetch manual pendente esmaecem a lista anterior (`pending`, D02, .dc.html:725-726). A ação
 *    do usuário vem antes do erro: o clique em "Tentar de novo" precisa de resposta visível.
 * 3. Erro com a lista dos argumentos atuais na tela: só a faixa (`error-stale`), que continua
 *    durante o refetch por invalidação.
 * 4. O resto é `ready`, inclusive o refetch por invalidação (excluir, cancelar, refetch tardio
 *    de 3 s): a lista atual se atualiza sem esmaecer.
 *
 * @param input - Flags do resultado da consulta da lista.
 * @returns O estado visual da lista.
 */
export function getMyStudiesListState({
  isLoading,
  isFetching,
  hasError,
  hasData,
  hasCurrentData,
  hasManualRefreshPending,
}: MyStudiesListStateInput): MyStudiesListState {
  const hasNoCurrentList = isLoading || !hasData || (!hasCurrentData && hasError);
  if (hasNoCurrentList) return hasError && !hasManualRefreshPending ? 'error-initial' : 'initial';

  const isChangingArgs = !hasCurrentData && isFetching;
  if (isChangingArgs || hasManualRefreshPending) return 'pending';

  return hasError ? 'error-stale' : 'ready';
}

/**
 * Estado visual da lista da aba Meus Estudos para o componente da lista (#1999).
 *
 * É puro: não usa estado nem efeito do React, só repassa para {@link getMyStudiesListState}, que
 * os testes cobrem em tabela. O prefixo `use` mantém o ponto de ligação previsto no plano.
 *
 * @param input - Flags do resultado da consulta da lista.
 * @returns O estado visual da lista.
 */
export default function useMyStudiesListState(input: MyStudiesListStateInput): MyStudiesListState {
  return getMyStudiesListState(input);
}
